/**
 * Track 4: "Közösség" — rankings and hall of fame, alliance, galactic events, notifications.
 * Every number comes from the server; the buttons are requests and the lists show the server's answer.
 */
import { useEffect, useState } from 'react';
import { RankingsPanel } from './community/RankingsPanel.tsx';
import { AlliancePanel } from './community/AlliancePanel.tsx';
import { EventsPanel } from './community/EventsPanel.tsx';
import { NotificationsPanel } from './community/NotificationsPanel.tsx';
import { fetchGalacticEvents } from '../api/social.ts';

const SECTIONS = [
  { id: 'rankings', label: 'Rangsor' },
  { id: 'alliance', label: 'Szövetség' },
  { id: 'events', label: 'Események' },
  { id: 'notifications', label: 'Értesítések' },
] as const;
type SectionId = (typeof SECTIONS)[number]['id'];

export function CommunityView() {
  const [section, setSection] = useState<SectionId>('rankings');
  const [openEvents, setOpenEvents] = useState(0);

  // A badge on "Események" when a decision is waiting
  useEffect(() => {
    let live = true;
    fetchGalacticEvents().then(r => { if (live) setOpenEvents(r.events.filter(e => e.canDecide && !e.chosen).length); }).catch(() => undefined);
    return () => { live = false; };
  }, [section]);

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="community-view">
      <div className="max-w-5xl mx-auto space-y-4">
        <h1 className="text-lg font-semibold text-slate-200">Közösség</h1>
        <div role="tablist" aria-label="Közösség szakaszai" className="flex gap-1 overflow-x-auto border-b border-slate-700/50">
          {SECTIONS.map(s => (
            <button key={s.id} role="tab" type="button" aria-selected={section === s.id} data-testid={`community-tab-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`nav-btn min-h-[44px] flex-shrink-0 ${section === s.id ? 'active' : ''}`}>
              {s.label}
              {s.id === 'events' && openEvents > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold px-1" aria-label={`${openEvents} döntésre váró esemény`}>{openEvents}</span>
              )}
            </button>
          ))}
        </div>
        {section === 'rankings' && <RankingsPanel />}
        {section === 'alliance' && <AlliancePanel />}
        {section === 'events' && <EventsPanel onChanged={n => setOpenEvents(n)} />}
        {section === 'notifications' && <NotificationsPanel />}
      </div>
    </div>
  );
}
